import {
    Combobox,
    Transition,
    ComboboxInput,
    ComboboxButton,
    ComboboxOptions,
    ComboboxOption,
} from '@headlessui/react';
import Avatar from 'flow/components/ui-components/Avatar';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Icon, { IconKey } from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import { getOwnCountryCode } from 'flow/selectors/selectors';
import { Extension } from 'flow/types/Extension';
import { Refer } from 'flow/types/Pbx';
import Queue from 'flow/types/Queue';

import { TypedMap } from 'flow/types/TypedMap';
import { extractName, isEmptyNullOrUndefined } from 'flow/utils';
import { convertNumberToE164 } from 'flow/utils/call-utils';
import { twMerge } from 'tailwind-merge';
import { fromJS, Map } from 'immutable';
import React, { Fragment, ReactNode, useRef, useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { Contact } from 'flow/types/Contact';
import { VariableSizeList } from 'react-window';
import ReactWindowRow from 'flow/components/react-window-row';

export interface ReferralNumberOption {
    id?: string;
    label: string;
    title?: string;
    iconKey?: IconKey;
    number?: string;
    image?: ReactNode;
    dto: any;
}

interface Props {
    /**
     * Names the field for assistive tech. The visible label sits outside this
     * component, so only the caller can say what this combobox is for.
     */
    ariaLabel?: string;
    referralNumber: ReferralNumberOption;
    onChange: (newValue: ReferralNumberOption) => void;
}

const ReferralNumberCombobox = ({
    referralNumber,
    onChange,
    ariaLabel,
}: Props) => {
    const { i18next } = useAppContext();
    const listRef = useRef<VariableSizeList>();

    const extensions = useAppSelector((state) =>
        state.extensions?.get('extensions'),
    );
    const extensionStatuses = useAppSelector((state) =>
        state.extensions?.get('statuses'),
    );
    const queues = useAppSelector((state) => state.pbx?.get('queues', Map()));
    const refers = useAppSelector((state) => state.pbx?.get('refers', Map()));
    const userCountryCode = useAppSelector(getOwnCountryCode);

    const numbers: ReferralNumberOption[] = [];

    extensions.forEach((extension: TypedMap<Extension>) => {
        const contact = extension.get('contact', Map()) as TypedMap<Contact>;
        if (!isEmptyNullOrUndefined(contact.get('fixed'))) {
            numbers.push({
                id: contact.getIn(['fixed', 'nationalFormat']),
                label: `${extractName(contact)} - ${contact.getIn([
                    'fixed',
                    'nationalFormat',
                ])}`,
                title: extractName(contact),
                iconKey: 'phone',
                number: contact.getIn(['fixed', 'nationalFormat']),
                image: (
                    <ContactAvatar
                        contact={contact}
                        status={extensionStatuses.get(extension.get('key'))}
                        lazyLoad={true}
                    />
                ),
                dto: contact.get('fixed'),
            });
        }

        if (!isEmptyNullOrUndefined(contact.get('mobile'))) {
            numbers.push({
                id: contact.getIn(['mobile', 'nationalFormat']),
                label: `${extractName(contact)} - ${contact.getIn([
                    'fixed',
                    'nationalFormat',
                ])}`,
                title: extractName(contact),
                iconKey: 'mobile',
                number: contact.getIn(['mobile', 'nationalFormat']),
                image: (
                    <ContactAvatar
                        contact={contact}
                        status={extensionStatuses.get(extension.get('key'))}
                        lazyLoad={true}
                    />
                ),
                dto: contact.get('mobile'),
            });
        }
    });

    queues.forEach((queue: TypedMap<Queue>) => {
        numbers.push({
            id: queue.getIn(['number', 'nationalFormat']),
            label: `${queue.get('description')} - ${queue.getIn([
                'number',
                'nationalFormat',
            ])}`,
            title: queue.get('description'),
            iconKey: 'phone',
            number: queue.getIn(['number', 'nationalFormat']),
            image: (
                <Avatar className="bg-pbx-huntgroup">
                    <Icon iconKey="queuelogin" />
                </Avatar>
            ),
            dto: queue.get('number'),
        });
    });

    refers.forEach((refer: TypedMap<Refer>) => {
        numbers.push({
            id: refer.getIn(['number', 'nationalFormat']),
            label: `${refer.get('description')} - ${refer.getIn([
                'number',
                'nationalFormat',
            ])}`,
            title: refer.get('description'),
            iconKey: 'phone',
            number: refer.getIn(['number', 'nationalFormat']),
            image: (
                <Avatar className="bg-pbx-refer">
                    <Icon iconKey="ivr" />
                </Avatar>
            ),
            dto: refer.get('number'),
        });
    });

    const sortedNumbers = numbers.sort((item1, item2) =>
        item1.label.localeCompare(item2.label, 'sv'),
    );

    const [query, setQuery] = useState('');

    const filteredNumbers =
        query === ''
            ? sortedNumbers
            : sortedNumbers.filter((number) =>
                  number.label
                      .toLowerCase()
                      .replace(/\s+/g, '')
                      .includes(query.toLowerCase().replace(/\s+/g, '')),
              );

    const renderAddOption = () => {
        const number = convertNumberToE164(query, userCountryCode);
        const label = i18next.t('status.unknown') + ' ' + number;

        return (
            <ComboboxOption
                key={number}
                className={({ focus }) =>
                    twMerge(
                        'relative cursor-default py-2 ps-3 pe-9 select-none',
                        focus ? 'bg-gray-200' : 'text-gray-900',
                    )
                }
                value={{
                    id: number,
                    label,
                    dto: fromJS({ number }),
                }}
            >
                <span className="truncate text-gray-500">{label}</span>
            </ComboboxOption>
        );
    };

    const createAllListItems = () =>
        filteredNumbers.map((option) => (
            <ComboboxOption
                key={option.id}
                className={({ focus }) =>
                    twMerge(
                        'relative cursor-default py-2 ps-3 pe-9 select-none',
                        focus ? 'bg-gray-200' : 'text-gray-900',
                    )
                }
                value={option}
            >
                {({ selected }) => (
                    <div className="flex items-center gap-2">
                        {option.image}
                        <div className="flex flex-col gap-0.5">
                            <span
                                className={twMerge(
                                    'truncate text-gray-500',
                                    selected && 'font-medium',
                                )}
                            >
                                {option.title}
                            </span>
                            <div className="flex items-center gap-1">
                                <Icon iconKey={option.iconKey} size="1rem" />
                                <span
                                    className={twMerge(
                                        'truncate text-gray-500',
                                        selected && 'font-medium',
                                    )}
                                >
                                    <pre>{option.dto.get('number')}</pre>
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </ComboboxOption>
        ));

    const listItems = createAllListItems() as any[];

    if (listRef.current) {
        // Reset the whole list item height cache here. We could calculate from which index to
        // clear the cache here but that would add extra complexity and will be hard to maintain,
        // so we go with this approach and will optimize it if we see that it's necessary.
        listRef.current.resetAfterIndex(0, false);
    }

    return (
        <Combobox value={referralNumber} by="id" onChange={onChange}>
            <div
                className="relative w-full text-sm/4"
                data-test-id="referral-number"
            >
                <ComboboxInput
                    aria-label={ariaLabel}
                    className="focus-ring w-full rounded-sm border-1 bg-gray-200 py-3 ps-3 pe-9 text-gray-900"
                    displayValue={(option: ReferralNumberOption) =>
                        option?.label
                    }
                    onChange={(event) => setQuery(event.target.value)}
                />

                <ComboboxButton className="absolute inset-y-0 end-0 flex items-center pe-2">
                    <Icon
                        className="size-5 text-gray-600"
                        iconKey="arrow_down"
                        aria-hidden="true"
                    />
                </ComboboxButton>

                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setQuery('')}
                >
                    <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                        <VariableSizeList
                            ref={listRef}
                            overscanCount={7}
                            itemData={listItems}
                            itemCount={listItems.length}
                            itemSize={() => 52}
                            className="scrollbar max-w-full"
                            height={filteredNumbers.length !== 0 ? 156 : 0} // shows 3 items on the list. Don't render more because of scrolling in modals
                            width={640}
                            style={{
                                direction: undefined,
                            }}
                        >
                            {ReactWindowRow}
                        </VariableSizeList>

                        {filteredNumbers.length === 0 &&
                            query &&
                            renderAddOption()}
                    </ComboboxOptions>
                </Transition>
            </div>
        </Combobox>
    );
};

export default ReferralNumberCombobox;
